/**
 * Validation Options Modal Component
 * Manages dgma_options for each validation rule
 */

import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Space, Tag } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { IConfigOption } from '../../../types/configOption';

interface ValidationOptionsModalProps {
  open: boolean;
  validationName: string;
  options: string[];
  allConfigOptions?: IConfigOption[];
  onClose: () => void;
  onSave: (options: string[]) => void;
  loading?: boolean;
}

export const ValidationOptionsModal: React.FC<ValidationOptionsModalProps> = ({
  open,
  validationName,
  options,
  allConfigOptions = [],
  onClose,
  onSave,
  loading = false,
}) => {
  const [optionsList, setOptionsList] = useState<string[]>(options);
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);

  useEffect(() => {
    setOptionsList(options);
  }, [options]);

  // Filter options with Number Input type (dogma_inputtype === 2)
  const numberInputOptions = allConfigOptions.filter(opt => opt.dogma_inputtype === 2);

  // Get option name by ID
  const getOptionName = (optionId: string): string => {
    const option = allConfigOptions.find(opt => opt.dogma_configurationoptionsid === optionId);
    return option?.dogma_name || optionId;
  };

  const handleAddOption = () => {
    if (selectedOptionIds.length > 0) {
      const newOptions = selectedOptionIds.filter(id => !optionsList.includes(id));
      setOptionsList([...optionsList, ...newOptions]);
      setSelectedOptionIds([]);
    }
  };

  const handleRemoveOption = (optionToRemove: string) => {
    setOptionsList(optionsList.filter(opt => opt !== optionToRemove));
  };

  const handleSave = () => {
    onSave(optionsList);
  };

  const handleCancel = () => {
    setOptionsList(options);
    setSelectedOptionIds([]);
    onClose();
  };

  return (
    <Modal
      title={`Setup Options - ${validationName}`}
      open={open}
      onCancel={handleCancel}
      onOk={handleSave}
      okText="Save Options"
      cancelText="Cancel"
      width={600}
      confirmLoading={loading}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Add Option</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              mode="multiple"
              placeholder="Select number input options"
              value={selectedOptionIds}
              onChange={setSelectedOptionIds}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={numberInputOptions
                .filter(opt => !optionsList.includes(opt.dogma_configurationoptionsid))
                .map(opt => ({
                  value: opt.dogma_configurationoptionsid,
                  label: `${opt['dogma_optiontype@OData.Community.Display.V1.FormattedValue']} - ${opt.dogma_name}`,
                }))}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddOption}
              disabled={selectedOptionIds.length === 0}
            >
              Add
            </Button>
          </Space.Compact>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Options ({optionsList.length})
          </label>
          <div className="border rounded p-3 min-h-[200px] max-h-[300px] overflow-y-auto">
            {optionsList.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No options added yet</p>
            ) : (
              <Space size={[8, 8]} wrap>
                {optionsList.map((optionId) => (
                  <Tag
                    key={optionId}
                    closable
                    closeIcon={<CloseOutlined />}
                    onClose={() => handleRemoveOption(optionId)}
                    className="px-3 py-1"
                  >
                    {getOptionName(optionId)}
                  </Tag>
                ))}
              </Space>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
